// src/contexts/CompanyContext.js
// ✅ Zero Firebase imports — delegates entirely to companyService → repo

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { companyService } from "../services/companyService";
import { useAuth }        from "./AuthContext";

const CompanyContext = createContext();

export function useCompanies() {
  const ctx = useContext(CompanyContext);
  if (!ctx) {
    throw new Error("[useCompanies] must be used inside <CompaniesProvider>.");
  }
  return ctx;
}

export function CompaniesProvider({ children }) {
  const { currentUser } = useAuth();

  const [companies, setCompanies] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);

  // ── Role-aware fetch ───────────────────────────────────────────────────────
  //
  //  superadmin  → all companies          (for management + dropdowns)
  //  admin       → their own company only (for reference / dropdowns)
  //  others      → empty                  (no company access needed)
  //
  const fetchCompanies = useCallback(async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let data = [];

      if (currentUser.role === "superadmin") {
        // superadmin sees ALL companies
        data = await companyService.getAllCompanies(currentUser);

      } else if (currentUser.companyId) {
        // admin / locationAdmin / regular user —
        // only need their own company for reference purposes
        const company = await companyService.getCompanyById(
          currentUser.companyId,
        );
        data = company ? [company] : [];
      }

      setCompanies(data ?? []);
    } catch (err) {
      console.error("[CompanyContext] fetchCompanies failed:", err);
      setError(err.message || "Failed to load companies.");
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // ── createCompany ──────────────────────────────────────────────────────────
  const createCompany = useCallback(
    async (companyData) => {
      if (!currentUser) {
        throw new Error("[CompanyContext] No authenticated user.");
      }
      try {
        const created = await companyService.createCompany(
          companyData,
          currentUser,
        );
        setCompanies((prev) => [...prev, created]);
        return created;
      } catch (err) {
        console.error("[CompanyContext] createCompany failed:", err);
        throw err;
      }
    },
    [currentUser],
  );

  // ── updateCompany ──────────────────────────────────────────────────────────
  const updateCompany = useCallback(
    async (docId, updateData) => {
      if (!currentUser) {
        throw new Error("[CompanyContext] No authenticated user.");
      }
      try {
        const updated = await companyService.updateCompany(
          docId,
          updateData,
          currentUser,
        );
        setCompanies((prev) =>
          prev.map((c) => (c.id === docId ? { ...c, ...updated } : c)),
        );
        return updated;
      } catch (err) {
        console.error("[CompanyContext] updateCompany failed:", err);
        throw err;
      }
    },
    [currentUser],
  );

  // ── deleteCompany ──────────────────────────────────────────────────────────
  const deleteCompany = useCallback(async (docId) => {
    try {
      await companyService.deleteCompany(docId);
      setCompanies((prev) => prev.filter((c) => c.id !== docId));
      return { success: true };
    } catch (err) {
      console.error("[CompanyContext] deleteCompany failed:", err);
      throw err;
    }
  }, []);

  // ── refreshCompanies ───────────────────────────────────────────────────────
  const refreshCompanies = useCallback(async () => {
    await fetchCompanies();
  }, [fetchCompanies]);

  // ── Context value ──────────────────────────────────────────────────────────
  const value = {
    companies,
    loading,
    error,
    createCompany,
    updateCompany,
    deleteCompany,
    refreshCompanies,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
}